import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, MessageCircle } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";
import { adminApi, formatCOP } from "../../lib/api";
import { formatUnit } from "../../lib/units";
import { toast } from "sonner";

const STATUSES = [
  { v: "recibido", l: "Recibido" },
  { v: "en_revision", l: "En revisión" },
  { v: "confirmado", l: "Confirmado" },
  { v: "enviado", l: "Enviado" },
  { v: "cancelado", l: "Cancelado" },
];

// Customer-facing WhatsApp message per status change.
const STATUS_MESSAGE = (order, estado) => {
  const cliente = order.razon_social || "cliente";
  const num = order.order_number;
  const total = formatCOP(order.total);
  switch (estado) {
    case "recibido":
      return `Hola ${cliente}, hemos recibido tu pedido ${num} por ${total}. ¡Gracias por tu compra!`;
    case "en_revision":
      return `Hola ${cliente}, tu pedido ${num} está siendo revisado por nuestro equipo. Te avisaremos en cuanto lo confirmemos.`;
    case "confirmado":
      return `Hola ${cliente}, tu pedido ${num} ha sido confirmado y está siendo alistado en bodega. Pronto lo despacharemos.`;
    case "enviado":
      return `Hola ${cliente}, tu pedido ${num} ya fue enviado y llegará pronto. Si tienes alguna duda, escríbenos por aquí.`;
    case "cancelado":
      return `Hola ${cliente}, lamentablemente tu pedido ${num} fue cancelado. Para más información contáctanos al equipo de CM.`;
    default:
      return `Hola ${cliente}, tu pedido ${num} cambió de estado a "${estado}".`;
  }
};

// Strip non-digits from a Colombian phone and ensure country prefix.
const buildWhatsAppUrl = (raw, message) => {
  if (!raw) return null;
  let digits = String(raw).replace(/\D/g, "");
  if (!digits) return null;
  // If only 10 digits, assume Colombia mobile and prefix 57
  if (digits.length === 10) digits = `57${digits}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
};

export default function AdminOrderDetail() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get(`/admin/orders/${orderId}`)
      .then((r) => { setOrder(r.data); setNewStatus(r.data.estado); })
      .catch((err) => toast.error(err?.response?.data?.detail || "Error al cargar el pedido"));
  }, [orderId]);

  const updateStatus = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.patch(`/admin/orders/${orderId}/status`, { estado: newStatus });
      setOrder(data);
      toast.success("Estado actualizado");
      // After saving, prompt the admin to notify the client via WhatsApp (single click)
      const message = STATUS_MESSAGE(data, newStatus);
      const url = buildWhatsAppUrl(data.telefono, message);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        toast.info("Cliente sin teléfono registrado para notificar por WhatsApp");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar");
    } finally { setSaving(false); }
  };

  const notifyCurrentStatus = () => {
    if (!order) return;
    const message = STATUS_MESSAGE(order, order.estado);
    const url = buildWhatsAppUrl(order.telefono, message);
    if (!url) { toast.error("El cliente no tiene teléfono registrado"); return; }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!order) return <div className="text-cm-ink-soft" data-testid="order-detail-loading">Cargando pedido...</div>;

  return (
    <div className="space-y-6" data-testid="admin-order-detail-page">
      <Link to="/admin/pedidos" className="inline-flex items-center text-sm text-cm-ink-soft hover:text-cm-red">
        <ArrowLeft className="h-4 w-4 mr-1" /> Volver a pedidos
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Pedido</p>
          <h1 className="font-mono text-2xl sm:text-3xl mt-1 text-cm-red tracking-tight" data-testid="order-detail-number">{order.order_number}</h1>
          <p className="text-xs text-cm-ink-soft mt-1">{new Date(order.created_at).toLocaleString("es-CO")}</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft mb-1">Estado</p>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger className="w-48 border-cm-border" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s.v} value={s.v} data-testid={`status-set-${s.v}`}>{s.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={updateStatus} disabled={saving || newStatus === order.estado} className="btn-cm-primary" data-testid="save-status-btn">
            <Save className="h-4 w-4 mr-1.5" /> Guardar y notificar
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={notifyCurrentStatus}
            className="border-[#25D366] text-[#1DA851] hover:bg-[#25D366]/10"
            data-testid="notify-whatsapp-btn"
            title="Reenviar mensaje al cliente sin cambiar estado"
          >
            <MessageCircle className="h-4 w-4 mr-1.5" /> Reenviar
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 bg-white border-cm-border shadow-none overflow-hidden">
          <div className="px-6 py-4 border-b border-cm-border">
            <h2 className="font-serif text-lg">Productos ({order.lineas?.length || 0})</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-cm-ink-soft bg-cm-cream-alt/40">
              <tr><th className="px-6 py-2.5">Código</th><th className="px-2 py-2.5">Descripción</th><th className="px-2 py-2.5 text-right">Cant.</th><th className="px-2 py-2.5 text-right">P. Unit</th><th className="px-6 py-2.5 text-right">Subtotal</th></tr>
            </thead>
            <tbody className="divide-y divide-cm-border">
              {order.lineas?.map((l) => (
                <tr key={l.codigo}>
                  <td className="px-6 py-3 font-mono text-xs">{l.codigo}</td>
                  <td className="px-2 py-3"><div className="line-clamp-2">{l.descripcion}</div><div className="text-xs text-cm-ink-soft">{l.categoria} · {formatUnit(l.unidad)}</div></td>
                  <td className="px-2 py-3 text-right font-mono">{l.cantidad}</td>
                  <td className="px-2 py-3 text-right font-mono">{formatCOP(l.precio_unitario)}</td>
                  <td className="px-6 py-3 text-right font-mono font-semibold">{formatCOP(l.subtotal)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-cm-cream-alt/40 text-sm">
              <tr><td colSpan={4} className="px-6 py-2 text-right text-cm-ink-soft">Subtotal</td><td className="px-6 py-2 text-right font-mono">{formatCOP(order.subtotal)}</td></tr>
              <tr><td colSpan={4} className="px-6 py-2 text-right text-cm-ink-soft">IVA</td><td className="px-6 py-2 text-right font-mono">{formatCOP(order.iva)}</td></tr>
              <tr className="border-t border-cm-border"><td colSpan={4} className="px-6 py-3 text-right font-serif text-lg">Total</td><td className="px-6 py-3 text-right font-mono text-xl font-bold text-cm-red" data-testid="order-detail-total">{formatCOP(order.total)}</td></tr>
            </tfoot>
          </table>
        </Card>

        <div className="space-y-4">
          <Card className="bg-white border-cm-border shadow-none p-5">
            <h3 className="font-serif text-base mb-3">Cliente</h3>
            <div className="space-y-1.5 text-sm" data-testid="order-customer-info">
              <p className="font-medium">{order.razon_social}</p>
              <p className="font-mono text-xs text-cm-ink-soft">NIT {order.nit}</p>
              <Separator className="my-2" />
              <p><span className="text-cm-ink-soft">Tel: </span><span className="font-mono">{order.telefono}</span></p>
              <p><span className="text-cm-ink-soft">Email: </span>{order.email}</p>
              <p><span className="text-cm-ink-soft">Dir: </span>{order.direccion}</p>
              <p><span className="text-cm-ink-soft">Ciudad: </span>{order.ciudad}</p>
              <Separator className="my-2" />
              <p><span className="text-cm-ink-soft">Lista: </span><Badge className="bg-cm-yellow/30 text-cm-ink hover:bg-cm-yellow/30">{order.lista_precios_nombre}</Badge></p>
            </div>
          </Card>

          <Card className="bg-white border-cm-border shadow-none p-5">
            <h3 className="font-serif text-base mb-2">Estado en Arcos</h3>
            <p className="text-sm" data-testid="arcos-sync-info">
              <span className={`font-mono text-xs uppercase ${order.arcos_sync_status === "synced" ? "text-green-700" : "text-amber-700"}`}>
                {order.arcos_sync_status === "synced" ? "● Sincronizado" : "○ Local (pendiente sync)"}
              </span>
            </p>
            {order.arcos_order_number && <p className="text-xs mt-1">N° Arcos: <span className="font-mono">{order.arcos_order_number}</span></p>}
            {order.arcos_error && <p className="text-xs text-cm-red mt-2 font-mono break-words">{order.arcos_error}</p>}
          </Card>

          {order.observaciones && (
            <Card className="bg-white border-cm-border shadow-none p-5">
              <h3 className="font-serif text-base mb-2">Observaciones del cliente</h3>
              <p className="text-sm whitespace-pre-wrap" data-testid="order-observations">{order.observaciones}</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
